// =============================================================================
// ClientContractsCard — mig 389 (25 Sep 2026)
// =============================================================================
// James, on the Ventia client page: "Add Contracts Library where we can load
// and save the MFA etc" — "add contracts to clients as documents for keep safe."
//
// The keep-safe for what belongs to the CLIENT rather than a job: the master
// agreement, contracts, rate cards, NDAs. Upload, list, open, mark superseded,
// and (director only) remove. File bytes go to the private `client-documents`
// bucket; the row in `client_documents` is the register. Manager+ see it —
// the bucket's policies are the same tests as the rows, so a link alone is
// worth nothing to a login that could not see the row.
//
// Kept deliberately plain: no AI read, no expiry chasing, no link to quotes.
// Those are their own pieces when James asks.
// =============================================================================

import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSignature, Upload, ExternalLink, Trash2, Archive, RotateCcw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Field, Input, Select, Pill } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import { downloadFromStorage } from "@/lib/storageDownload";

const BUCKET = "client-documents";

const DOC_TYPES: { value: string; label: string }[] = [
  { value: "msa",                  label: "Master agreement (MSA / MFA)" },
  { value: "contract",             label: "Contract" },
  { value: "rate_card",            label: "Rate card / schedule of rates" },
  { value: "purchase_order_terms", label: "PO terms & conditions" },
  { value: "nda",                  label: "NDA" },
  { value: "insurance",            label: "Insurance / compliance they hold" },
  { value: "other",                label: "Other" },
];
const typeLabel = (v: string) => DOC_TYPES.find(t => t.value === v)?.label ?? v;

interface ClientDoc {
  client_document_id: string;
  doc_type: string;
  title: string;
  description: string | null;
  file_name: string;
  file_path: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  effective_from: string | null;
  expires_on: string | null;
  reference: string | null;
  is_current: boolean;
  uploaded_at: string;
}

function fmtBytes(n: number | null): string {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
// Local day keys only — never toISOString().slice(0,10).
function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ClientContractsCard({ clientId }: { clientId: string }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canManage  = hasRole(user, "manager");
  const isDirector = hasRole(user, "director");

  const [adding, setAdding]   = useState(false);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [showOld, setShowOld] = useState(false);
  const [file, setFile]       = useState<File | null>(null);
  const [form, setForm]       = useState({ doc_type: "msa", title: "", reference: "", effective_from: "", expires_on: "", description: "" });
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["client_documents", clientId],
    enabled: !!clientId && canManage,
    queryFn: async () => {
      // client_documents is new in 389 — database.types.ts hasn't been regened.
      const { data, error } = await (supabase as any)
        .from("client_documents")
        .select("client_document_id, doc_type, title, description, file_name, file_path, file_size_bytes, mime_type, effective_from, expires_on, reference, is_current, uploaded_at")
        .eq("client_id", clientId)
        .order("is_current", { ascending: false })
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ClientDoc[];
    },
  });

  const current = docs.filter(d => d.is_current);
  const older   = docs.filter(d => !d.is_current);

  function reset() {
    setAdding(false); setFile(null); setError(null);
    setForm({ doc_type: "msa", title: "", reference: "", effective_from: "", expires_on: "", description: "" });
    if (fileRef.current) fileRef.current.value = "";
  }

  async function save() {
    if (!file) { setError("Choose the file first."); return; }
    const title = form.title.trim() || file.name.replace(/\.[^.]+$/, "");
    setBusy(true); setError(null);
    try {
      // {client_id}/{yyyy-mm-dd}-{safe name}: one folder per client in the bucket,
      // dated so two versions of the same agreement never collide.
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
      const path = `${clientId}/${todayKey()}-${Date.now().toString(36)}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
      if (upErr) throw upErr;

      const { error: insErr } = await (supabase as any).from("client_documents").insert({
        client_id:       clientId,
        doc_type:        form.doc_type,
        title,
        description:     form.description.trim() || null,
        reference:       form.reference.trim() || null,
        effective_from:  form.effective_from || null,
        expires_on:      form.expires_on || null,
        file_name:       file.name,
        file_path:       path,
        file_size_bytes: file.size,
        mime_type:       file.type || null,
        uploaded_by:     (user as any)?.user_id ?? null,
      });
      if (insErr) throw insErr;
      qc.invalidateQueries({ queryKey: ["client_documents", clientId] });
      reset();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally { setBusy(false); }
  }

  async function open(d: ClientDoc) {
    setError(null);
    try { await downloadFromStorage(BUCKET, d.file_path, d.file_name); }
    catch (e: any) { setError(e?.message ?? String(e)); }
  }

  async function setCurrent(d: ClientDoc, is_current: boolean) {
    setBusy(true); setError(null);
    const { error } = await (supabase as any).from("client_documents")
      .update({ is_current }).eq("client_document_id", d.client_document_id);
    setBusy(false);
    if (error) { setError(error.message); return; }
    qc.invalidateQueries({ queryKey: ["client_documents", clientId] });
  }

  // Director only, and it asks. The file goes too — this is the one place a
  // contract is actually removed, so it should be rare and deliberate.
  async function remove(d: ClientDoc) {
    if (!window.confirm(`Remove "${d.title}" and its file? A superseded agreement is better marked as no longer current than removed.`)) return;
    setBusy(true); setError(null);
    try {
      const { error: sErr } = await supabase.storage.from(BUCKET).remove([d.file_path]);
      if (sErr) throw sErr;
      const { error: dErr } = await (supabase as any).from("client_documents")
        .delete().eq("client_document_id", d.client_document_id);
      if (dErr) throw dErr;
      qc.invalidateQueries({ queryKey: ["client_documents", clientId] });
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally { setBusy(false); }
  }

  if (!canManage) return null;

  const Row = ({ d }: { d: ClientDoc }) => {
    const expired = !!d.expires_on && d.expires_on < todayKey();
    return (
      <div className="py-2.5 flex items-start gap-2">
        <FileSignature size={13} className="text-text-muted mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <button type="button" onClick={() => open(d)}
                  className="text-[12px] text-text hover:text-accent text-left truncate block w-full" title={d.file_name}>
            {d.title} <ExternalLink size={10} className="inline ml-0.5 opacity-60" />
          </button>
          <div className="text-[10.5px] text-text-muted mt-0.5 flex flex-wrap gap-x-2">
            <span>{typeLabel(d.doc_type)}</span>
            {d.reference && <span className="font-mono">{d.reference}</span>}
            {d.effective_from && <span>from {fmtDate(d.effective_from)}</span>}
            {d.expires_on && <span className={expired ? "text-danger" : ""}>{expired ? "expired" : "to"} {fmtDate(d.expires_on)}</span>}
            {d.file_size_bytes ? <span>{fmtBytes(d.file_size_bytes)}</span> : null}
          </div>
          {d.description && <div className="text-[11px] text-text-dim mt-0.5">{d.description}</div>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {d.is_current
            ? <button type="button" title="Mark as no longer current (keeps the file)" disabled={busy}
                      onClick={() => setCurrent(d, false)} className="p-1 rounded text-text-muted hover:text-warn"><Archive size={12}/></button>
            : <button type="button" title="Mark as current again" disabled={busy}
                      onClick={() => setCurrent(d, true)} className="p-1 rounded text-text-muted hover:text-ok"><RotateCcw size={12}/></button>}
          {isDirector && (
            <button type="button" title="Remove (director only)" disabled={busy}
                    onClick={() => remove(d)} className="p-1 rounded text-text-muted hover:text-danger"><Trash2 size={12}/></button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-text-muted">
            <FileSignature size={12}/> Contracts &amp; agreements
          </div>
          {!adding && (
            <Button size="sm" variant="secondary" icon={Upload} onClick={() => setAdding(true)} disabled={busy}>Add</Button>
          )}
        </div>

        {adding && (
          <div className="mb-3 border border-line rounded p-3 space-y-2 bg-ink">
            <Field label="File">
              <input ref={fileRef} type="file"
                     accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                     onChange={e => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !form.title) setForm(s => ({ ...s, title: f.name.replace(/\.[^.]+$/, "") })); }}
                     className="block w-full text-[12px] text-text-muted" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Kind">
                <Select value={form.doc_type} onChange={(e: any) => setForm(s => ({ ...s, doc_type: e.target.value }))}>
                  {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </Field>
              <Field label="Their reference" hint="Contract / MSA number as the client calls it">
                <Input value={form.reference} onChange={(e: any) => setForm(s => ({ ...s, reference: e.target.value }))} placeholder="e.g. VEN-MSA-2026-014" />
              </Field>
            </div>
            <Field label="Title">
              <Input value={form.title} onChange={(e: any) => setForm(s => ({ ...s, title: e.target.value }))} placeholder="Master Services Agreement 2026" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="In force from">
                <Input type="date" mono value={form.effective_from} onChange={(e: any) => setForm(s => ({ ...s, effective_from: e.target.value }))} />
              </Field>
              <Field label="Expires (if it does)">
                <Input type="date" mono value={form.expires_on} onChange={(e: any) => setForm(s => ({ ...s, expires_on: e.target.value }))} />
              </Field>
            </div>
            <Field label="Notes">
              <Input value={form.description} onChange={(e: any) => setForm(s => ({ ...s, description: e.target.value }))} placeholder="What it covers, anything to watch" />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="secondary" onClick={reset} disabled={busy}>Cancel</Button>
              <Button size="sm" icon={Upload} onClick={save} disabled={busy || !file}>{busy ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-[11px] text-text-muted py-2">Loading…</div>
        ) : current.length === 0 && older.length === 0 ? (
          <div className="text-[11px] text-text-muted italic py-1">
            Nothing on file. Load the MSA, contracts and rate cards here so they're one click away from every job.
          </div>
        ) : (
          <div className="divide-y divide-line">
            {current.map(d => <Row key={d.client_document_id} d={d} />)}
          </div>
        )}

        {older.length > 0 && (
          <div className="mt-2 pt-2 border-t border-line">
            <button type="button" onClick={() => setShowOld(v => !v)} className="text-[11px] text-text-muted hover:text-text">
              {showOld ? "Hide" : "Show"} {older.length} no longer current
            </button>
            {showOld && <div className="divide-y divide-line opacity-70">{older.map(d => <Row key={d.client_document_id} d={d} />)}</div>}
          </div>
        )}

        {error && (
          <div className="mt-2 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{error}</div>
        )}
        <div className="mt-2 text-[10px] text-text-dim flex items-center gap-1">
          <Pill tone="notice" label="managers & directors" small /> <span>Private bucket; remove is director-only.</span>
        </div>
      </div>
    </Card>
  );
}
